import { AdPlatform, CampaignType, CompositeId } from '@app/core/models/common.models';
import { Primitive } from '@app/core/utilities/typescript.utilities';
import {
  CriterionAdditionCampaignType,
  CriterionAdditionCriterionSubtype,
  CriterionAdditionCriterionType,
  CriterionAdditionProposalType,
} from '@app/criterion-addition/shared/models/criterion-addition.models';
import { DestinationInstance, DestinationSource } from '@app/criterion-addition/shared/models/destination.models';
import { ProductAdSyncMode } from '@app/criterion-addition/shared/models/product-ad-sync.models';

export enum RuleLevel {
  Default = 'DEFAULT',
  Platform = 'PLATFORM',
  Account = 'ACCOUNT',
  Campaign = 'CAMPAIGN',
  AdGroup = 'ADGROUP',
}

export enum RuleAction {
  Add = 'ADD',
  Reject = 'REJECT',
  Review = 'REVIEW',
}

export enum RuleEditMode {
  New = 'NEW',
  Edit = 'EDIT',
  Duplicate = 'DUPLICATE',
}

export enum RuleFilterOperand {
  Domain = 'DOMAIN',
  Keyword = 'KEYWORD',
  KeywordLength = 'KEYWORD-LENGTH',
  KeywordWordCount = 'KEYWORD-WORD-COUNT',
}

export enum RuleFilterOperation {
  Contains = 'CONTAINS',
  ContainsWord = 'CONTAINS-WORD',
  EndsWith = 'ENDS-WITH',
  Equals = 'EQUALS',
  GreaterThan = 'GREATER-THAN',
  GreaterThanOrEqual = 'GREATER-THAN-OR-EQUAL',
  LessThan = 'LESS-THAN',
  LessThanOrEqual = 'LESS-THAN-OR-EQUAL',
  NotContains = 'NOT-CONTAINS',
  NotContainsWord = 'NOT-CONTAINS-WORD',
  NotEndsWith = 'NOT-ENDS-WITH',
  NotEquals = 'NOT-EQUALS',
  NotStartsWith = 'NOT-STARTS-WITH',
  StartsWith = 'STARTS-WITH',
}

export interface RuleType {
  ruleTypeId: number;
  platform: AdPlatform;
  campaignType: CriterionAdditionCampaignType;
  proposalType: CriterionAdditionProposalType;
  criterionType: CriterionAdditionCriterionType;
  criterionSubtype: CriterionAdditionCriterionSubtype;
  enabled: boolean;
}

export interface RuleLocation {
  level: RuleLevel;
  platform: AdPlatform;
  // searchable
  adspertAccountId: number;
  // searchable
  accountId: string;
  // searchable
  accountName: string;
  accountPlatform: AdPlatform;
  adspertCampaignId: CompositeId;
  // searchable
  campaignId: number;
  // searchable
  campaignName: string;
  campaignType: CampaignType;
  adspertAdgroupId: CompositeId;
  // searchable
  adgroupId: number;
  adgroupName: string;
}

export interface ActiveRuleSet {
  ruleTypeId: number;
  [RulesTab.Platform]?: AdPlatform;
  [RulesTab.Account]?: number;
  [RulesTab.Campaign]?: CompositeId;
  [RulesTab.Adgroup]?: CompositeId;
}

export interface RuleFilter {
  operand: RuleFilterOperand;
  operation: RuleFilterOperation;
  values: string[];
}

export interface RuleDestination extends DestinationInstance {
  // searchable
  adgroupId: number | void;
  createdAt: Date;
}

export interface RuleInstance extends DestinationSource<RuleDestination> {
  ruleId: number;
  action: RuleAction;
  filters: RuleFilter[];
  catchAll: boolean;
  definedAt: RuleLocation;

  // non-api
  initExpanded?: boolean;
}

export interface RuleSet {
  ruleTypeId: number;
  definedAt: RuleLocation;
  inherited?: boolean;
  rules: RuleInstance[];
}

export interface Rule {
  location: RuleLocation;
  rulesets: RuleSet[];
  // searchable
  campaignId: number;
  // searchable
  campaignName: string;
  // searchable
  adgroupId: number;
  // searchable
  adgroupName: string;
  productAdSyncMode: ProductAdSyncMode | void;
  createdAt: Date;
}

export interface SupportedCriteria {
  platform: AdPlatform;
  isSupported: boolean;
  data: { key: CriterionAdditionCriterionType; data: string[] }[];
}

export enum RulesTab {
  Platform = 0,
  Account = 1,
  Campaign = 2,
  Adgroup = 3,
}
export type RulesSelectedTabSortValue = Record<string, Primitive>;

export interface RulesTabData {
  id: string;
  title: any;
  active: boolean;
  tab: string | RulesTab;
}

export interface RuleSetsWithCampaignType {
  type: CriterionAdditionCampaignType;
  userDefinedRules: boolean;
  rulesets: RuleSetWithRuleType[];
}

export interface CampaignTypeGroupedRule {
  location: RuleLocation;
  hasUserDefinedRules: boolean;
  campaignTypeRulesets: RuleSetsWithCampaignType[];
}

export interface RuleSetWithRuleType extends RuleSet {
  campaignType: CriterionAdditionCampaignType;
}

export interface CampaignAdGroupTabRule {
  location: RuleLocation;
  userDefinedRules: boolean;
  rulesets: RuleSetWithRuleType[];
}

export interface SortOption {
  translation: string;
  key: string;
  id: number;
}

export const MapTabToColumnNames = {
  [RulesTab.Platform]: 'platform',
  [RulesTab.Account]: 'adspertAccountId',
  [RulesTab.Campaign]: 'adspertCampaignId',
  [RulesTab.Adgroup]: 'adspertAdgroupId',
};
